import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ChatMessageNotification } from './model/chat-message-notification.model';
import { ChatMessage } from './model/chat-message.model';
import { ChatNotification } from './model/chat-notification.model';
import { ChatRequest } from './model/chat-request.model';
import { Chat } from './model/chat.model';
import { HubMethods } from './model/hub-methods.model';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
export class ChatStatusService implements OnDestroy {
    private readonly _chatSubj: Subject<ChatNotification>;
    readonly chat: Observable<ChatNotification>;

    private readonly _messageSubj: Subject<ChatMessageNotification>;
    readonly message: Observable<ChatMessageNotification>;

    private readonly _didCreateSub: Subscription;

    constructor(private readonly _socketService: SocketService) {
        this._chatSubj = new Subject();
        this.chat = this._chatSubj.asObservable();

        this._messageSubj = new Subject();
        this.message = this._messageSubj.asObservable();

        this._bind();
        this._didCreateSub = this._socketService.didCreate
            .pipe(
                tap(() => {
                    this._bind();
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._didCreateSub?.unsubscribe();
        this._chatSubj?.complete();
        this._messageSubj?.complete();
    }

    async retrieve(request: ChatRequest): Promise<void> {
        await this._socketService.send(HubMethods.RetrieveChat, request);
    }

    async updateChat(chat: Chat): Promise<void> {
        await this._socketService.send(HubMethods.UpdateChat, chat);
    }

    async listMessages(chat: Chat): Promise<void> {
        await this._socketService.send(HubMethods.ListChatMessages, chat);
    }

    async sendMessage(chat: Chat, message: ChatMessage): Promise<void> {
        await this._socketService.send(
            HubMethods.SendChatMessage,
            chat,
            message,
        );
    }

    async deleteMessage(chat: Chat, message: ChatMessage): Promise<void> {
        await this._socketService.send(
            HubMethods.DeleteChatMessage,
            chat,
            message,
        );
    }

    private _bind(): void {
        this._socketService.on(
            HubMethods.OnChat,
            (payload: ChatNotification): void => {
                this._chatSubj.next(payload);
            },
        );

        this._socketService.on(
            HubMethods.OnChatMessage,
            (payload: ChatMessageNotification): void => {
                this._messageSubj.next(payload);
            },
        );

        this._socketService.on(
            HubMethods.OnChatMessageList,
            (payload: ChatMessageNotification): void => {
                this._messageSubj.next(payload);
            },
        );
    }
}
